import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Stack,
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
  Input,
  Textarea,
  Select,
  NumberInput,
  NumberInputField,
  HStack,
  useToast,
} from "@chakra-ui/react";
import { ModalShell } from "../../../shared/ui";
import { isPlanoBloqueado } from "../../../shared/api/planoBloqueado";
import { vinculoSchema, ENDERECO_MAX } from "../schemas/vinculoSchema";
import { useCreateVinculoMutation } from "../hooks/useCreateVinculoMutation";
import { useUpdateVinculoMutation } from "../hooks/useUpdateVinculoMutation";
import { TIPOS_VINCULO } from "../api/vinculosApi";
import VinculoColorPicker from "./VinculoColorPicker";

const EMPTY_VALUES = {
  nome: "",
  tipo: "privado",
  cnpj: "",
  valorPlantao: 1400,
  valorFimDeSemana: "",
  horasPlantao: 12,
  diaPagamento: 10,
  diaLimiteNF: 5,
  horaInicioPadrao: "",
  cor: "",
  endereco: "",
};

function valuesFromVinculo(vinculo) {
  if (!vinculo) return EMPTY_VALUES;
  return {
    nome: vinculo.nome,
    tipo: vinculo.tipo,
    cnpj: vinculo.cnpj ?? "",
    valorPlantao: vinculo.valor_plantao,
    valorFimDeSemana: vinculo.valor_fim_de_semana ?? "",
    horasPlantao: vinculo.horas_plantao ?? 12,
    diaPagamento: vinculo.dia_pagamento,
    diaLimiteNF: vinculo.dia_limite_nf,
    horaInicioPadrao: vinculo.hora_inicio_padrao ?? "",
    cor: vinculo.cor ?? "",
    endereco: vinculo.endereco ?? "",
  };
}

export default function NovoVinculoModal({ isOpen, onClose, vinculo = null }) {
  const toast = useToast();
  const createMutation = useCreateVinculoMutation();
  const updateMutation = useUpdateVinculoMutation();
  const mutation = vinculo ? updateMutation : createMutation;

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(vinculoSchema),
    defaultValues: EMPTY_VALUES,
  });

  const corEscolhida = watch("cor");

  useEffect(() => {
    if (isOpen) reset(valuesFromVinculo(vinculo));
  }, [isOpen, vinculo, reset]);

  const fechar = () => {
    reset(EMPTY_VALUES);
    onClose();
  };

  const onSubmit = (data) => {
    mutation.mutate(vinculo ? { id: vinculo.id, ...data } : data, {
      onSuccess: (salvo) => {
        toast({
          title: vinculo ? `Vínculo "${salvo.nome}" atualizado` : `Vínculo "${salvo.nome}" criado`,
          status: "success",
          duration: 4000,
        });
        fechar();
      },
      onError: (error) => {
        // 402: quem fala com o médico é o PlanoBloqueadoModal (com o botão de assinar).
        if (isPlanoBloqueado(error)) return;
        toast({
          title: vinculo ? "Não foi possível atualizar o vínculo" : "Não foi possível criar o vínculo",
          description: error.response?.data?.mensagem,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      },
    });
  };

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={fechar}
      title={vinculo ? "Editar vínculo" : "Novo vínculo"}
      onSubmit={handleSubmit(onSubmit)}
      submitLabel={vinculo ? "Salvar alterações" : "Criar vínculo"}
      isSubmitting={mutation.isPending}
    >
      <Stack spacing={4}>
        <FormControl isInvalid={Boolean(errors.nome)}>
          <FormLabel>Nome do hospital/tomador</FormLabel>
          <Input autoFocus placeholder="Hospital São Lucas" {...register("nome")} />
          <FormErrorMessage>{errors.nome?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={Boolean(errors.tipo)}>
          <FormLabel>Tipo</FormLabel>
          <Select {...register("tipo")}>
            {TIPOS_VINCULO.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </Select>
          <FormErrorMessage>{errors.tipo?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={Boolean(errors.cor)}>
          <FormLabel as="div" id="vinculo-cor-label">
            Cor na agenda
          </FormLabel>
          <VinculoColorPicker
            value={corEscolhida}
            onChange={(cor) => setValue("cor", cor, { shouldDirty: true, shouldValidate: true })}
          />
          {vinculo ? null : (
            <FormHelperText>Sem escolha, usamos uma cor que você ainda não usa em outro vínculo.</FormHelperText>
          )}
          <FormErrorMessage>{errors.cor?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={Boolean(errors.cnpj)}>
          <FormLabel>CNPJ (opcional)</FormLabel>
          <Input placeholder="00.000.000/0001-00" {...register("cnpj")} />
          <FormErrorMessage>{errors.cnpj?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={Boolean(errors.valorPlantao)}>
          <FormLabel>Valor por plantão (R$)</FormLabel>
          <NumberInput min={0} precision={2}>
            <NumberInputField {...register("valorPlantao")} />
          </NumberInput>
          <FormErrorMessage>{errors.valorPlantao?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={Boolean(errors.valorFimDeSemana)}>
          <FormLabel>Valor no fim de semana (R$, opcional)</FormLabel>
          <NumberInput min={0} precision={2}>
            <NumberInputField placeholder="Mesmo valor" {...register("valorFimDeSemana")} />
          </NumberInput>
          <FormHelperText>Sugerido nos plantões que começam no sábado ou no domingo. Não muda plantões já registrados.</FormHelperText>
          <FormErrorMessage>{errors.valorFimDeSemana?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={Boolean(errors.horasPlantao)}>
          <FormLabel>Duração padrão do plantão (horas)</FormLabel>
          <NumberInput min={0.5} max={24} precision={1} step={0.5}>
            <NumberInputField {...register("horasPlantao")} />
          </NumberInput>
          <FormErrorMessage>{errors.horasPlantao?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={Boolean(errors.horaInicioPadrao)}>
          <FormLabel>Hora de início padrão (opcional)</FormLabel>
          <Input type="time" {...register("horaInicioPadrao")} />
          <FormHelperText>Já vem preenchida ao registrar um plantão e vale para a escala fixa.</FormHelperText>
          <FormErrorMessage>{errors.horaInicioPadrao?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={Boolean(errors.endereco)}>
          <FormLabel>Endereço (opcional)</FormLabel>
          <Textarea
            rows={2}
            maxLength={ENDERECO_MAX}
            placeholder="Av. Paulista, 1000 - Bela Vista, São Paulo/SP"
            {...register("endereco")}
          />
          <FormHelperText>Usado no atalho "Como chegar" (Google Maps e Waze).</FormHelperText>
          <FormErrorMessage>{errors.endereco?.message}</FormErrorMessage>
        </FormControl>

        <HStack spacing={4} align="flex-start">
          <FormControl isInvalid={Boolean(errors.diaPagamento)}>
            <FormLabel>Paga dia</FormLabel>
            <NumberInput min={1} max={31}>
              <NumberInputField {...register("diaPagamento")} />
            </NumberInput>
            <FormErrorMessage>{errors.diaPagamento?.message}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={Boolean(errors.diaLimiteNF)}>
            <FormLabel>NF até dia</FormLabel>
            <NumberInput min={1} max={31}>
              <NumberInputField {...register("diaLimiteNF")} />
            </NumberInput>
            <FormErrorMessage>{errors.diaLimiteNF?.message}</FormErrorMessage>
          </FormControl>
        </HStack>
      </Stack>
    </ModalShell>
  );
}
